import { apiGet, apiPost, apiPut } from "./http";
import type { FeedbackItem, FeedbackStatus, FeedbackType } from "./types";

const KNOWN_TYPES = new Set<string>(["试用申请", "功能建议", "Bug反馈", "活动建议", "会员服务", "其他"]);

export function normalizeFeedbackType(t: string): FeedbackType {
  if (KNOWN_TYPES.has(t)) return t as FeedbackType;
  const map: Record<string, FeedbackType> = {
    general: "其他",
    suggestion: "功能建议",
    bug: "Bug反馈",
    activity: "活动建议",
    service: "会员服务",
  };
  return map[t] ?? "其他";
}

export function normalizeFeedbackStatus(s: string): FeedbackStatus {
  if (s === "pending") return "unread";
  if (s === "unread" || s === "processing" || s === "resolved") return s;
  return "unread";
}

export function mapFeedbackApiRow(row: {
  id: string;
  type: string;
  content: string;
  email: string;
  submittedAt: string;
  status: string;
  reply?: string | null;
  repliedAt?: string | null;
  handledByLabel?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
}): FeedbackItem {
  return {
    id: row.id,
    type: normalizeFeedbackType(row.type),
    content: row.content,
    email: row.email ?? "",
    submittedAt: row.submittedAt,
    status: normalizeFeedbackStatus(row.status),
    reply: row.reply ?? undefined,
    repliedAt: row.repliedAt ?? undefined,
    handledByLabel: row.handledByLabel ?? undefined,
    tenantId: row.tenantId,
    tenantName: row.tenantName,
  };
}

export async function fetchTenantFeedbackItems(tenantLegacyId: string): Promise<FeedbackItem[]> {
  const rows = await apiGet<
    {
      id: string;
      type: string;
      content: string;
      email: string;
      submittedAt: string;
      status: string;
      reply?: string | null;
      repliedAt?: string | null;
      handledByLabel?: string | null;
    }[]
  >(`/api/tenants/${tenantLegacyId}/feedback-items`);
  return rows.map(mapFeedbackApiRow);
}

export async function fetchPlatformFeedbackItems(tenantLegacyId?: string): Promise<FeedbackItem[]> {
  const q = tenantLegacyId ? `?tenantLegacyId=${encodeURIComponent(tenantLegacyId)}` : "";
  const rows = await apiGet<
    {
      id: string;
      type: string;
      content: string;
      email: string;
      submittedAt: string;
      status: string;
      reply?: string | null;
      repliedAt?: string | null;
      handledByLabel?: string | null;
      tenantId?: string | null;
      tenantName?: string | null;
    }[]
  >(`/api/feedback-items${q}`);
  return rows.map(mapFeedbackApiRow);
}

export async function putTenantFeedbackItem(
  tenantLegacyId: string,
  itemLegacyId: string,
  body: { status?: FeedbackStatus; reply?: string; handledByLabel?: string | null }
) {
  await apiPut(`/api/tenants/${tenantLegacyId}/feedback-items/${encodeURIComponent(itemLegacyId)}`, body);
}

export async function putPlatformScopedFeedbackItem(
  itemLegacyId: string,
  body: { status?: FeedbackStatus; reply?: string; handledByLabel?: string | null }
) {
  await apiPut(`/api/feedback-items/${encodeURIComponent(itemLegacyId)}`, body);
}

export async function submitTenantFeedback(
  tenantLegacyId: string,
  body: { type: string; content: string; email?: string }
) {
  await apiPost(`/api/tenants/${tenantLegacyId}/feedback-items`, body);
}
